<i18n>
  {
    "fr": {
      "departure": "Depuis",
      "arrival": "Vers",
      "errors": {
        "departure-airport-required": "Le lieu de départ est requis",
        "arrival-airport-required": "Le lieu de retour est requis"
      }
    },
    "en": {
      "departure": "From",
      "arrival": "To",
      "errors": {
        "departure-airport-required": "Departure place is required",
        "arrival-airport-required": "Arrival place is required"
      }
    }
  }
</i18n>

<template>
  <div>
    <v-autocomplete
      :ref="isDeparture ? 'flight-autocomplete-departure-airport' : 'flight-autocomplete-return-airport'"
      v-model="selectedAirport"
      :data-cy="isDeparture ? 'flight-autocomplete-departure-airport' : 'flight-autocomplete-return-airport'"
      :items="airportsList"
      item-text="airportName"
      item-value="airportObject"
      return-object
      :search-input.sync="searchInput"
      :loading="isLoading"
      :label="label"
      :error-messages="errors"
      color="primary"
      background-color="primaryLight"
      solo
      flat
      hide-no-data
      auto-select-first
      append-icon=""
      class="custom-autocomplete"
      @input="selectedAirportTouch"
      @blur="selectedAirportTouch"
      @focus="clearSelection"
    >
      <template #prepend-inner>
        <v-icon
          v-if="isDeparture"
          color="primary"
          class="mr-2"
        >
          mdi-airplane-takeoff
        </v-icon>
        <v-icon
          v-else-if="!isDeparture"
          color="primary"
          class="mr-2"
        >
          mdi-airplane-landing
        </v-icon>
      </template>
      <template
        #selection="{ item }"
        @click="clearSelection"
      >
        <v-tooltip top>
          <template #activator="{on, attrs}">
            <span
              v-bind="attrs"
              v-on="on"
            >
              {{ formatSelection(item) }}
            </span>
          </template>
          <span>{{ formatAirport(item) }}</span>
        </v-tooltip>
      </template>
      <template #item="data">
        <v-list-item-content
          :data-cy="isDeparture ? `flight-autocomplete-departure-airport${data.item.id}` : `flight-autocomplete-return-airport${data.item.id}`"
          v-text="data.item.airportName"
        />
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'FlightAutoCompleteAirport',
  mixins: [validationMixin],
  props: {
    isDeparture: {
      type: Boolean,
      default: true,
      required: false
    }
  },
  data: () => ({
    searchInput: ''
  }),
  computed: {
    ...mapState({
      departureAirport: state => state.fb.searchParameters.departureAirport,
      departureAirportEntries: state => state.fb.searchParameters.departureAirportEntries,
      departureAirportLoadingState: state => state.fb.searchParameters.departureAirportLoading,
      arrivalAirport: state => state.fb.searchParameters.arrivalAirport,
      arrivalAirportEntries: state => state.fb.searchParameters.arrivalAirportEntries,
      arrivalAirportLoadingState: state => state.fb.searchParameters.arrivalAirportLoading
    }),
    /**
     * Return loading state of departure or arrival airport based on isDeparture prop
     * @return {boolean}
     */
    isLoading () {
      let loading
      if (this.isDeparture === true) {
        loading = this.departureAirportLoadingState
      } else {
        loading = this.arrivalAirportLoadingState
      }
      return loading
    },
    selectedAirport: {
      /**
       * Return selected airport from vueX store based on isDeparture prop
       * @return {Object}
       */
      get () {
        let selectedAirport
        if (this.isDeparture === true) {
          selectedAirport = this.departureAirport
        } else if (this.isDeparture === false) {
          selectedAirport = this.arrivalAirport
        }
        return selectedAirport
      },
      /**
       * Change selected airport in vueX store based on isDeparture prop
       * @param newValue {Object}
       */
      set (newValue) {
        if (this.isDeparture === true) {
          this.changeDepartureAirport(newValue)
        } else {
          this.changeArrivalAirport(newValue)
        }
      }
    },
    entries: {
      /**
       * Return the autocomplete entries based on the isDeparture props
       * @return {Object[]}
       */
      get () {
        let selectedEntries
        if (this.isDeparture === true) {
          selectedEntries = this.departureAirportEntries
        } else {
          selectedEntries = this.arrivalAirportEntries
        }
        return selectedEntries
      },
      /**
       * Change the entries in the store (this is required for the swap departure and arrival airport method to work properly)
       * @param newValue {Object}
       */
      set (newValue) {
        if (this.isDeparture) {
          this.changeDepartureAirportEntries(newValue)
        } else {
          this.changeArrivalAirportEntries(newValue)
        }
      }
    },
    /**
     * This computed take autocomplete entries and map a new variable to each object of the list, this variable format the airport name
     * @return {Object}
     */
    airportsList () {
      return this.entries.map(entry => {
        const airportName = this.formatAirport(entry)
        return Object.assign({}, entry, { airportName })
      })
    },
    /**
     * Change the label of the autocomplete based on isDeparture prop
     * @return {string}
     */
    label () {
      let sentence
      if (this.isDeparture) {
        sentence = this.$i18n.t('departure')
      } else {
        sentence = this.$i18n.t('arrival')
      }
      return sentence
    },
    /**
     * Return the opposite of vuelidate invalid parameters (to improve readability of code)
     * This value need to be computed for the watcher to work properly
     * @return {boolean}
     */
    isValid () {
      // This value need to be computed for the watcher to work properly
      return !this.$v.$invalid
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    errors () {
      const errors = []
      if (!this.$v.selectedAirport.$dirty) return errors
      if (this.isDeparture) {
        !this.$v.selectedAirport.required && errors.push(this.$i18n.t('errors.departure-airport-required'))
      } else {
        !this.$v.selectedAirport.required && errors.push(this.$i18n.t('errors.arrival-airport-required'))
      }
      return errors
    }
  },
  watch: {
    /**
     * This watcher work with the searchInput.sync of the v-autocomplete
     * if the string has a length higher than 3, it start a new search request
     * (based on isDeparture prop)
     * @param newVal {string}
     */
    searchInput (newVal) {
      if (this.searchInput !== null && this.searchInput.length >= 3) {
        if (this.isDeparture) {
          this.searchDepartureAirports(newVal)
        } else {
          this.searchArrivalAirports(newVal)
        }
      }
    },
    /**
     * Watch for value change of validity then commit the new value to the store
     * (based on isDeparture prop)
     * @param newVal {boolean}
     */
    isValid (newVal) {
      if (this.isDeparture) {
        this.changeDepartureAirportValidity(newVal)
      } else {
        this.changeArrivalAirportValidity(newVal)
      }
    }
  },
  validations: {
    selectedAirport: {
      required
    }
  },
  methods: {
    ...mapMutations({
      // Departure
      changeDepartureAirport: 'fb/searchParameters/changeDepartureAirport',
      changeDepartureAirportEntries: 'fb/searchParameters/changeDepartureAirportEntries',
      changeDepartureAirportValidity: 'fb/searchParameters/changeDepartureAirportValidity',
      // Arrival
      changeArrivalAirport: 'fb/searchParameters/changeArrivalAirport',
      changeArrivalAirportEntries: 'fb/searchParameters/changeArrivalAirportEntries',
      changeArrivalAirportValidity: 'fb/searchParameters/changeArrivalAirportValidity'
    }),
    ...mapActions({
      searchDepartureAirports: 'fb/searchParameters/searchDepartureAirports',
      searchArrivalAirports: 'fb/searchParameters/searchArrivalAirports'
    }),
    /**
     * Take an airport object as an input and return a string with a specific format
     * (name - city - country - IATA)
     * @param airport {Object}
     * @return {string}
     */
    formatAirport (airport) {
      let formatedAirport = ''

      if (airport.code) {
        formatedAirport = airport.name + ' - ' + airport.city + ' - ' + airport.country + ' (' + airport.code + ')'
      } else {
        // check for french city name, international instead
        let city = ''
        if (airport.city_name_fr) {
          city = airport.city_name_fr
        } else {
          city = airport.city_name
        }
        // check for french country name, international instead
        let country = ''
        if (airport.country) {
          if (airport.country.name_fr) {
            country = airport.country.name_fr
          } else {
            country = airport.country.name
          }
        }
        formatedAirport = airport.name + ' - ' + city + ' - ' + country + ' (' + airport.iata_code + ')'
      }
      return formatedAirport
    },
    /**
     * Simply take an airport object to format it and then cut the string if its length is higher than 22 chars.
     * @param airport {Object}
     * @return {string}
     */
    formatSelection (airport) {
      let sentence = this.formatAirport(airport)
      if (sentence.length < 22) {
        return sentence
      } else {
        sentence = sentence.substr(0, 25) + '...'
        return sentence
      }
    },
    /**
     * Simply reset all the data needed for the autocomplete to empty state
     */
    clearSelection () {
      this.searchInput = ''
      this.entries = []
      this.selectedAirport = undefined
    },
    /**
     * Shortcut for vuelidate touch method
     */
    selectedAirportTouch () {
      this.$v.selectedAirport.$touch()
    }
  }
}
</script>

<style scoped>
.custom-placeholder-color >>> input::placeholder {
  color: rgba(14, 32, 38, 0.40) !important;
  opacity: 1;
}

.custom-autocomplete >>> input {
  min-width: 0px !important;
}
</style>
